/**
 * `/sign-in` — THE COLD-START DOOR.
 *
 * THE ROUTE NAME IS OURS, and it is worth saying so because a document says otherwise.
 * docs/ui/04-pages.md proposes `/sign-in` and labels the row "ruled, adr/0007", which
 * overstates: ADR-0007 fixes the FLOW and fixes `/enter`, and `/enter` is the ONLY cabinet route
 * the corpus names. Layout, wording and every other route name are ours (decision-log row 99).
 *
 * THIS IS NOT THE COMMON CASE. "THE COMMON CASE IS NOT A FRESH SIGN-IN": most people arrive through
 * the hand-off at ./enter.tsx, already signed in inside the phone app. This screen is the cold
 * start — a new browser, a cleared cache, someone who never used the app
 * (adr/0007-web-app-stack.md).
 *
 * WHAT IT TALKS TO. Nothing, at the moment of signing in: the SPA talks to Firebase Auth directly
 * and no `authors-api` operation is involved. Only afterwards does it read `GET /v1/me` once and
 * route on the answer. That is why a blocked popup has no HTTP status anywhere in this file.
 *
 * EVERY STATE ON THIS SCREEN IS RENDERED, none is a toast:
 *   booting · idle · popup opening · POPUP BLOCKED · cancelled (silently back to idle) ·
 *   provider unreachable · signed in and reading /v1/me · 503 RECONNECTING (session held) ·
 *   401 signed out · anonymous session needing a link · already signed in.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Anchor, Button, Divider, Group, Stack, Text, TextInput } from '@mantine/core';
import { ErrorState, LoadingState } from '@moonx/ui';
import { AuthShell } from '../../features/auth/components/AuthShell';
import { IdentityUnavailablePanel } from '../../features/auth/components/IdentityUnavailablePanel';
import { LinkAccountPanel } from '../../features/auth/components/LinkAccountPanel';
import { MagicLinkPanel } from '../../features/auth/components/MagicLinkPanel';
import { PopupBlockedPanel } from '../../features/auth/components/PopupBlockedPanel';
import { ProviderButtons } from '../../features/auth/components/ProviderButtons';
import { WEBSITE_ORIGIN } from '../../features/auth/handoff/website-handoff';
import { browserNavigation, CABINET_HOME_PATH } from '../../features/auth/navigation';
import type { Navigation } from '../../features/auth/navigation';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import {
  forgetMagicLinkAddress,
  looksLikeEmailAddress,
  recallMagicLinkAddress,
} from '../../features/auth/session/magic-link-address';
import { needsProviderLink } from '../../features/auth/session/session-state';
import type { PopupProvider } from '../../features/auth/firebase/auth-port';
import { Trans, useTranslation } from '../../i18n';

export interface SignInRouteProps {
  /**
   * Called once the person is signed in and their session has loaded. Wire it to the router and
   * this screen gets out of the way; leave it out and it renders a Continue button instead, which
   * is the honest behaviour while the cabinet's own routes are still being built.
   */
  readonly onSignedIn?: () => void;
  readonly navigation?: Navigation;
}

export function SignInRoute({ onSignedIn, navigation = browserNavigation }: SignInRouteProps) {
  const { t } = useTranslation('auth');
  const {
    state,
    secondsUntilRetry,
    signInWithProvider,
    sendMagicLink,
    completeMagicLink,
    magicLinkCallback,
    linkAnonymousAccount,
    linkError,
    retryNow,
    signOut,
  } = useAuthSession();

  const [busyProvider, setBusyProvider] = useState<PopupProvider | null>(null);
  const [linkBusyProvider, setLinkBusyProvider] = useState<PopupProvider | null>(null);

  const choose = (provider: PopupProvider): void => {
    setBusyProvider(provider);
    void signInWithProvider(provider).finally(() => {
      setBusyProvider(null);
    });
  };

  const link = (provider: PopupProvider): void => {
    setLinkBusyProvider(provider);
    void linkAnonymousAccount(provider).finally(() => {
      setLinkBusyProvider(null);
    });
  };

  const ready = state.phase === 'ready';
  const linkNeeded = needsProviderLink(state);

  // ALREADY SIGNED IN ON THIS ORIGIN — the SDK restored a session, so this screen has nothing to
  // ask. Firebase sessions are per-origin (adr/0002), which is exactly why this only covers the
  // cabinet's own origin and never the website's.
  useEffect(() => {
    if (ready && !linkNeeded && onSignedIn !== undefined) {
      onSignedIn();
    }
  }, [ready, linkNeeded, onSignedIn]);

  const providerButtons = <ProviderButtons onChoose={choose} busyProvider={busyProvider} />;

  if (state.phase === 'booting') {
    return (
      <AuthShell title={t('signIn.bootingTitle')}>
        <LoadingState label={t('signIn.loadingLabel')} lines={2} />
      </AuthShell>
    );
  }

  // 503, 429, or no network. THE SESSION IS HELD. Never a sign-out — that is the whole point of
  // adr/0010's 401/503 split.
  if (state.phase === 'reconnecting') {
    return (
      <AuthShell title={t('common.oneMoment')}>
        <IdentityUnavailablePanel
          secondsUntilRetry={secondsUntilRetry}
          onRetryNow={retryNow}
          sessionHeld={state.session !== null}
        />
      </AuthShell>
    );
  }

  if (state.phase === 'refused') {
    return (
      <AuthShell title={t('signIn.refused.title')}>
        <ErrorState
          onRetry={retryNow}
          secondaryAction={
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                void signOut();
              }}
            >
              {t('signIn.refused.someoneElse')}
            </Button>
          }
        >
          <Text size="sm">{t('signIn.refused.body')}</Text>
        </ErrorState>
      </AuthShell>
    );
  }

  if (state.phase === 'loading-session') {
    return (
      <AuthShell title={t('opening.title')} standfirst={t('opening.checking')}>
        <LoadingState label={t('opening.loadingLabel')} lines={3} />
      </AuthShell>
    );
  }

  if (ready && linkNeeded) {
    // G30. A prompt, never a refusal, and never a second account.
    return (
      <AuthShell title={t('signIn.link.title')} standfirst={t('signIn.link.standfirst')}>
        <LinkAccountPanel onLink={link} busyProvider={linkBusyProvider} error={linkError} />
      </AuthShell>
    );
  }

  if (ready) {
    return (
      <AuthShell title={t('common.signedInTitle')}>
        <Stack gap="md">
          <Text size="sm">
            {t('signIn.signedInAs', {
              name:
                state.identity?.displayName ??
                state.identity?.email ??
                t('signIn.yourMoonXAccount'),
            })}
          </Text>
          <Group gap="sm">
            <Button
              variant="filled"
              onClick={() => {
                if (onSignedIn !== undefined) {
                  onSignedIn();
                  return;
                }
                navigation.push(CABINET_HOME_PATH);
              }}
            >
              {t('common.continueToCabinet')}
            </Button>
            <Button
              variant="subtle"
              onClick={() => {
                void signOut();
              }}
            >
              {/* Local only: the cabinet holds no server session and this surface has no sign-out route. */}
              {t('signIn.signOut')}
            </Button>
          </Group>
        </Stack>
      </AuthShell>
    );
  }

  // From here down the person is signed out.
  if (magicLinkCallback) {
    return (
      <AuthShell title={t('signIn.magicLink.title')}>
        <MagicLinkHandler onFinish={completeMagicLink} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('signIn.title')} standfirst={t('signIn.standfirst')}>
      <Stack gap="lg">
        {state.signInFailure === 'popup-blocked' ? (
          <PopupBlockedPanel retry={providerButtons} />
        ) : (
          <Stack gap="lg">
            {providerButtons}
            {state.signInFailure === 'provider-unreachable' ? (
              <ErrorState
                title={t('signIn.providerUnreachable.title')}
                onRetry={() => {
                  choose('google');
                }}
                retryLabel={t('signIn.providerUnreachable.retry')}
              >
                <Text size="sm">{t('signIn.providerUnreachable.body')}</Text>
              </ErrorState>
            ) : null}
            {state.signInFailure === 'unknown' ? (
              <ErrorState title={t('signIn.unknownFailure.title')}>
                <Text size="sm">{t('signIn.unknownFailure.body')}</Text>
              </ErrorState>
            ) : null}
          </Stack>
        )}

        <MagicLinkPanel onSend={sendMagicLink} disabled={busyProvider !== null} />

        <Divider />

        {/*
          THE WEBSITE → CABINET LEG, from the cabinet's side.

          The mechanism is already designed and needs no new operation: `ClientKind` already seeds
          `web`, so a visitor signed in on moonx.app mints a code with `client: "web"` and lands on
          /enter exactly like the phone app does. What the CABINET cannot do is check whether that
          visitor has a session — Firebase sessions are per-origin (adr/0002) — so this is a link
          to the website's own origin and not a promise about what is there. Two things are still
          owed and both are reported: one CORS line for POST /v1/handoff/codes, and confirmation
          that moonx.app actually carries a Firebase session today, which is a fact about a shipped
          site and a client concern under decision-log row 99.
        */}
        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            {t('signIn.newToMoonX')}
          </Text>
          <Text size="sm" c="dimmed">
            <Trans
              t={t}
              i18nKey="signIn.fromTheWebsite"
              components={{ anchor: <Anchor href={WEBSITE_ORIGIN} size="sm" rel="noopener" /> }}
            />
          </Text>
          <Text size="sm" c="dimmed">
            {t('signIn.fromTheApp')}
          </Text>
        </Stack>
      </Stack>
    </AuthShell>
  );
}

/**
 * The magic-link handler, folded into this route rather than given one of its own.
 *
 * ADR-0007 asks only for "a handler route in the SPA that finishes the sign-in". Folding it in here
 * is the smaller design: the address the link was sent to is stored locally and read back on this
 * same screen, and the fallback when it is missing — asking for the address again — is the same
 * field this screen already has.
 *
 * "THE STORED ADDRESS IS MISSING" IS A NORMAL PATH, NOT AN ERROR. It happens whenever the link is
 * opened on a different device or in a different browser, which is common.
 */
function MagicLinkHandler({ onFinish }: { readonly onFinish: (email: string) => Promise<void> }) {
  const { t } = useTranslation('auth');
  // Read once, at mount. Storage does not change under us during this screen, and re-reading it on
  // every render would restart the automatic attempt.
  const [remembered] = useState<string | null>(() => recallMagicLinkAddress());
  const [email, setEmail] = useState(remembered ?? '');
  const [busy, setBusy] = useState(remembered !== null);
  // Which sentence the field shows, worded at render so a language change redraws it.
  const [error, setError] = useState<'not-an-address' | 'link-failed' | null>(null);

  const finish = useCallback(
    (address: string): void => {
      if (!looksLikeEmailAddress(address)) {
        setError('not-an-address');
        return;
      }
      setError(null);
      setBusy(true);
      void onFinish(address)
        .then(() => {
          forgetMagicLinkAddress();
        })
        .catch(() => {
          setError('link-failed');
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [onFinish],
  );

  // StrictMode double-invokes this in development, so the attempt is guarded rather than assumed
  // to run once.
  const attempted = useRef(false);
  useEffect(() => {
    if (remembered === null || attempted.current) {
      return;
    }
    attempted.current = true;
    finish(remembered);
  }, [remembered, finish]);

  if (remembered !== null && error === null) {
    return <LoadingState label={t('signIn.loadingLabel')} lines={2} />;
  }

  return (
    <Stack gap="md">
      <Text size="sm">{t('signIn.magicLink.confirm')}</Text>
      <TextInput
        type="email"
        label={t('magicLink.emailLabel')}
        value={email}
        onChange={(event) => {
          setEmail(event.currentTarget.value);
          setError(null);
        }}
        error={
          error === null
            ? null
            : error === 'not-an-address'
              ? t('magicLink.notAnAddress')
              : t('signIn.magicLink.linkFailed')
        }
        disabled={busy}
        autoComplete="email"
      />
      <Button
        variant="filled"
        loading={busy}
        onClick={() => {
          finish(email);
        }}
      >
        {t('signIn.magicLink.finish')}
      </Button>
    </Stack>
  );
}

/*
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the door is drawn as the
 * prototype's sign-in dialog - `AuthShell`'s card over the stars, the two provider buttons full
 * width (`ProviderButtons`), the e-mail link as a quiet line under them (`MagicLinkPanel`) - and in
 * plain words: "the platform would not accept that request" is "we could not open your cabinet",
 * and the line for people coming from the app no longer prints the hand-off's address. Written
 * down here because the lines above are cited by number.
 */
